import {Injectable} from '@angular/core';
import {MmKeyboardStateService} from './keyboard-state.service';
import {ListenerGroup, getActiveEditable, onKeyboardViewportChange} from './shared/keyboard-viewport.util';

const APP_HEIGHT_VAR = '--mydocma-app-height';
const OVERLAY_CONTAINER_SELECTOR = '.cdk-overlay-container';
const VH_SIZED_PANE_SELECTOR = '.cdk-overlay-pane.mydocma-mm-fault-pane-dialog';
const VH_UNIT = '100vh';
const PINNED_HEIGHT_KEY = 'mmKbVhHeight';
const RESIZED_LAYOUT_MIN_PX = 150;
// iOS animates the keyboard with intermediate viewport resizes below the open threshold — treat the
// viewport as "no keyboard" (safe to capture) only while the gap is smaller than this
const STABLE_GAP_MAX_PX = 50;

// Tablet dialogs size themselves from --mydocma-app-height, which tracks the visual viewport — so the
// keyboard shrinks and re-centers them. While the keyboard is open we pin the var locally on the CDK
// overlay container to its last keyboard-closed value: dialogs keep their size and the keyboard overlaps.
@Injectable({providedIn: 'root'})
export class MmKeyboardModalFreezeService {

  private listeners = new ListenerGroup();
  private closedAppHeight = '';

  constructor(private readonly keyboardState: MmKeyboardStateService) {}

  start(): void {
    if (this.listeners.active || typeof window === 'undefined' || !window.visualViewport) {
      return;
    }
    this.captureClosedAppHeight();
    onKeyboardViewportChange(this.listeners, () => this.update());
  }

  // pin already at editable focus, not first at keyboard-open: the keyboard show animation resizes the
  // viewport in steps below the open threshold, and those frames would briefly squeeze the dialog (and
  // poison the captured height) before the state flips
  private update(): void {
    if (this.keyboardState.isKeyboardOpen) {
      this.freeze();
      return;
    }
    this.captureClosedAppHeight();
    // hold the pin while the viewport is still shrunk even though the state says closed: the keyboard
    // outlives the class whenever focus leaves the field first (blur to the dialog container, phantom
    // guard), and releasing there hands the dialog the keyboard-sized var — it resizes to the visible
    // band inside a full-height window and the layout looks torn in two until the viewport returns.
    // A readonly input keeps focus WITHOUT a keyboard, and holding for it froze a value captured mid
    // keyboard-hide (the var refresh races this listener) — the dialog stayed cut at the keyboard line
    // until the next interaction. No keyboard can be pending behind readonly focus: don't hold for it.
    const focused = getActiveEditable();
    const holdsKeyboard = !!focused && !(focused as HTMLInputElement).readOnly;
    if (holdsKeyboard || !this.isViewportStable()) {
      this.freeze();
    } else {
      this.release();
    }
  }

  private freeze(): void {
    if (!this.closedAppHeight) {
      return;
    }
    this.getOverlayContainers().forEach(container => container.style.setProperty(APP_HEIGHT_VAR, this.closedAppHeight));
    this.pinVhSizedPanes();
  }

  private release(): void {
    this.restoreVhSizedPanes();
    this.getOverlayContainers().forEach(container => container.style.removeProperty(APP_HEIGHT_VAR));
  }

  // the fault pane dialog is sized with 100vh, not the app-height var — and Android WebViews resize
  // the layout viewport with the keyboard, so vh shrinks and the pane re-centers inside the pinned
  // wrapper. While frozen AND the layout has provably shrunk, the vh unit in its inline height is
  // swapped for the var the container pin already holds steady; the original inline value comes back
  // on release. The shrink condition is what keeps this off everywhere else: browsers keep 100vh at
  // the large viewport (bigger than the measured var), so an unconditional swap would resize the pane
  // on iPad Safari / Android Chrome, which never had the bug. No rect capture: the gap-based
  // stability check cannot tell "keyboard away" on the resize model, a measured height could be
  // poisoned, the pinned var cannot.
  private pinVhSizedPanes(): void {
    if (!this.layoutShrunkBelowPinned()) {
      return;
    }
    this.getVhSizedPanes().forEach(pane => {
      const height = pane.style.height;
      if (height && height.includes(VH_UNIT) && !pane.dataset[PINNED_HEIGHT_KEY]) {
        pane.dataset[PINNED_HEIGHT_KEY] = height;
        pane.style.height = height.split(VH_UNIT).join(`var(${APP_HEIGHT_VAR})`);
      }
    });
  }

  private layoutShrunkBelowPinned(): boolean {
    const pinned = parseFloat(this.closedAppHeight);
    return !isNaN(pinned) && pinned - document.documentElement.clientHeight > RESIZED_LAYOUT_MIN_PX;
  }

  private restoreVhSizedPanes(): void {
    this.getVhSizedPanes().forEach(pane => {
      const original = pane.dataset[PINNED_HEIGHT_KEY];
      if (!original) {
        return;
      }
      delete pane.dataset[PINNED_HEIGHT_KEY];
      // keyboard-dialog-fit rewrites the height on rotation — if the inline value is no longer the
      // pinned one, that write is newer and restoring would resurrect the pre-rotation size
      if (pane.style.height.includes(APP_HEIGHT_VAR)) {
        pane.style.height = original;
      }
    });
  }

  private getVhSizedPanes(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>(VH_SIZED_PANE_SELECTOR));
  }

  private isViewportStable(): boolean {
    const vv = window.visualViewport;
    return !!vv && Math.round(document.documentElement.clientHeight - vv.height) <= STABLE_GAP_MAX_PX;
  }

  private captureClosedAppHeight(): void {
    if (!this.isViewportStable()) {
      return;
    }
    const value = getComputedStyle(document.documentElement).getPropertyValue(APP_HEIGHT_VAR).trim();
    if (value && this.matchesViewport(value)) {
      this.closedAppHeight = value;
    }
  }

  // the viewport can be back to full while the var still holds the keyboard-sized value — app-container
  // refreshes it from its own listener, which can run after this one. Measured on iPad: closing the
  // keyboard with its dismiss key restored vv to 1047 while the var still read 707, and capturing there
  // pinned 707 onto the containers, collapsing the dialog until the next interaction. A value that
  // disagrees with the viewport it is supposed to describe is stale by definition, so it is skipped and
  // the previous good one is kept.
  private matchesViewport(value: string): boolean {
    const vv = window.visualViewport;
    const height = parseFloat(value);
    if (!vv || isNaN(height)) {
      return true;
    }
    return Math.abs(height - vv.height) <= STABLE_GAP_MAX_PX;
  }

  private getOverlayContainers(): HTMLElement[] {
    // the app has more than one overlay container (app-container package + CDK default) and
    // dialogs are split between them — pin every one, or dialogs in the unpinned ones keep shrinking
    return Array.from(document.querySelectorAll<HTMLElement>(OVERLAY_CONTAINER_SELECTOR));
  }
}
